/**
 * Field ID Helper Utilities
 *
 * Provides utility functions for working with field IDs in linked tables,
 * particularly for handling relationship field prefixes and UUID extraction.
 *
 * @module fieldIdHelpers
 */

import { CALCULATED_TABLE_ALIAS, type TableFieldApiResponse } from '@loader/shared-types';

/**
 * UUID validation regex (RFC 4122 compliant)
 */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Extract pure UUID from a field ID
 *
 * Handles both prefixed (alias.UUID) and non-prefixed (UUID) formats.
 * For relationship fields, extracts just the UUID portion.
 *
 * @param fieldId - Field ID (can be "alias.UUID" or just "UUID")
 * @returns Pure UUID string
 * @throws Error if the extracted value is not a valid UUID
 *
 * @example
 * extractUUID('123e4567-e89b-12d3-a456-426614174000') // Returns same UUID
 * extractUUID('customers.123e4567-e89b-12d3-a456-426614174000') // Returns UUID only
 */
export function extractUUID(fieldId: string): string {
  if (fieldId.includes('.')) {
    const parts = fieldId.split('.');
    return parts[parts.length - 1]; // Return last part (UUID)
  }
  return fieldId; // Already just a UUID
}

/**
 * Validate that a string is a valid UUID format
 *
 * @param value - String to validate
 * @returns True if the value is a valid UUID
 *
 * @example
 * isValidUUID('123e4567-e89b-12d3-a456-426614174000') // true
 * isValidUUID('not-a-uuid') // false
 */
export function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Construct API field ID for backend calls
 *
 * All fields use the format: alias.UUID when the field has a table_alias.
 * The primary table (tables[0]) is not special - it uses its alias just like any other table.
 *
 * This function:
 * 1. Extracts the pure UUID from the field ID (handles prefixed and non-prefixed)
 * 2. Validates the UUID format
 * 3. Re-prefixes with the correct alias if the field has a table_alias
 * 4. Returns just the UUID if no table_alias
 *
 * @param fieldId - Field ID to construct API field ID from
 * @param selectedField - Field metadata to determine if it needs alias prefix
 * @returns Properly formatted field ID for API calls
 * @throws Error if the field ID contains an invalid UUID
 *
 * @example
 * // Field without alias (calculated column)
 * constructApiFieldId(
 *   '123e4567-e89b-12d3-a456-426614174000',
 *   { id: '...', ... }
 * )
 * // Returns: '123e4567-e89b-12d3-a456-426614174000'
 *
 * @example
 * // Field with table alias
 * constructApiFieldId(
 *   'customers.123e4567-e89b-12d3-a456-426614174000',
 *   { id: '...', table_alias: 'customers', ... }
 * )
 * // Returns: 'customers.123e4567-e89b-12d3-a456-426614174000'
 */
export function constructApiFieldId(
  fieldId: string,
  selectedField?: TableFieldApiResponse
): string {
  if (!fieldId || !selectedField) {
    return fieldId;
  }

  // Extract pure UUID from field ID (handles both prefixed and non-prefixed)
  const pureUUID = extractUUID(fieldId);

  // Validate UUID format
  if (!isValidUUID(pureUUID)) {
    throw new Error(
      `Invalid UUID format after extraction: ${pureUUID} (from ${fieldId})`
    );
  }

  // If field has a table_alias, prefix with the alias
  // All tables (including the primary table) use their alias
  if (selectedField.table_alias) {
    return `${selectedField.table_alias}.${pureUUID}`;
  }

  // For fields without table_alias (e.g., calculated columns), use just the UUID
  return pureUUID;
}

/**
 * Determine source type based on field metadata
 *
 * Helps the backend optimize the query path for fetching field values.
 * Now returns 'table' for all table fields (primary or relationship)
 * and 'calculated' for calculated columns.
 *
 * @param field - Field metadata
 * @returns Source type: 'table' for table fields, 'calculated' for calculated columns
 *
 * @example
 * determineSourceType({ table_alias: 'orders', ... }) // Returns 'table'
 * determineSourceType({ table_alias: 'customers', ... }) // Returns 'table'
 * determineSourceType({ table_alias: '__calculated__', ... }) // Returns 'calculated'
 * determineSourceType({ ... }) // Returns 'calculated' (no table_alias)
 */
export function determineSourceType(
  field?: TableFieldApiResponse
): 'table' | 'calculated' {
  if (!field) {
    return 'table';
  }

  // Calculated columns have table_alias === CALCULATED_TABLE_ALIAS or no table_alias
  if (!field.table_alias || field.table_alias === CALCULATED_TABLE_ALIAS) {
    return 'calculated';
  }

  return 'table';
}

